import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CircleHelp, Spotlight } from 'lucide-react';
import type { User } from 'firebase/auth';
import ViewHeader from './ViewHeader';
import { TutorialsPage, type GuideBridge } from './Forum';
import { HOW_TO } from '../data/topics';
import Spotlights, { type LibraryBridge } from './Spotlights';
import Upcoming from './Upcoming';
import MostStarred from './MostStarred';
import Newest from './Newest';
import RecentlyWatched from './RecentlyWatched';
import { requestAccount, useGuest } from '../guestMode';
import TopCreators from './TopCreators';
import SponsorPage from './SponsorPage';
import PromoteModal from './PromoteModal';
import ReferralPanel from './ReferralPanel';
import { useSubscription } from '../hooks/useSubscription';
import type { LivePromotion } from '../api/promotions';

/** How long the version strip stays in view, untouched, before the page eases back over it. */
const STRIP_PEEK_MS = 1500;

/** How many clips the feed adds each time the reader reaches the bottom —
 *  eight rows of three, so there is always more below the fold. */
const PAGE = 24;

/** How close to the foot counts as "at the bottom", in pixels. */
const NEAR_BOTTOM_PX = 600;

type Tab = 'new' | 'popular' | 'upcoming' | 'recent';

/** The feed's sections, in the order they sit across the tab row. History is
 *  the reader's own, so it needs an account. */
const TABS: { id: Tab; label: string; account?: boolean }[] = [
  { id: 'new', label: 'New' },
  { id: 'popular', label: 'Popular' },
  { id: 'recent', label: 'History', account: true },
  { id: 'upcoming', label: 'Upcoming' },
];

interface HomeProps {
  user: User;
  /** Library behaviour for the rails and creator pages (play/star/details). */
  library?: LibraryBridge;
  /** What the guides' live controls do, from the shell that owns that state. */
  guide?: GuideBridge;
  /** A creator to open, asked for from elsewhere in the app (a player's credit
   *  line). `n` changes with each ask, so the same creator can be asked for
   *  twice. */
  creatorRequest?: { handle: string; n: number } | null;
  /** Open the How To screen, asked for from another tab's header. Bumps
   *  with each ask, so it can be asked for twice. */
  howToRequest?: number;
}

// Primary landing page: a ViewHeader (with the How To button) over the
// Spotlight rail of creators, then one tabbed feed — New, Popular, History,
// Upcoming — three clips across, growing downward as the reader scrolls.
// Opening a screen (How To, a creator) hides all of it so it gets the full
// surface.
export default function Home({ user, library, guide, creatorRequest, howToRequest = 0 }: HomeProps) {
  const [howToOpen, setHowToOpen] = useState(howToRequest > 0);
  // Top Creators screen: closed, the list, or one creator's page.
  const [creators, setCreators] = useState<{ open: boolean; handle?: string }>(
    // Mounted to show a creator (a player's credit line from another tab):
    // open straight onto them.
    creatorRequest ? { open: true, handle: creatorRequest.handle } : { open: false },
  );
  // Which section the feed is showing, and how far down it the reader has
  // pulled. The feed replaced the old rails' "See all" screens: there is
  // nothing left to see all of when the section is the whole page.
  const [tab, setTab] = useState<Tab>('new');
  const [shown, setShown] = useState(PAGE);
  // A promoted card opens its sponsor's page rather than a creator page — a
  // sponsor may have no clips, and useRankedCreators gives no page to someone
  // under two.
  const [sponsor, setSponsor] = useState<LivePromotion | null>(null);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const { guest } = useGuest();
  // A guest has no subscription, and the stub user Home renders with has no
  // real uid — subscribing anyway opened a users/<stub> listener that the rules
  // deny for a signed-out client, and the retry storm tripped the Firestore
  // SDK's internal assertions (INTERNAL ASSERTION FAILED, ID: ca9/b815).
  const sub = useSubscription(guest ? null : user);
  const detailOpen = howToOpen || creators.open || sponsor !== null;

  // Asked for a creator while already showing: close whatever screen is up
  // and open theirs. Applied as the request arrives (state from props, in
  // render) rather than in an effect, so there is no frame of the old screen.
  const [seenHowTo, setSeenHowTo] = useState(howToRequest);
  if (howToRequest !== seenHowTo) {
    setSeenHowTo(howToRequest);
    if (howToRequest > 0) { setCreators({ open: false }); setHowToOpen(true); }
  }
  const [seenRequest, setSeenRequest] = useState(creatorRequest);
  if (creatorRequest !== seenRequest) {
    setSeenRequest(creatorRequest);
    if (creatorRequest) {
      setHowToOpen(false);
      setCreators({ open: true, handle: creatorRequest.handle });
    }
  }

  // Start with the version strip scrolled out of view above the rails. Runs
  // whenever the rails come back (a detail screen closing remounts them).
  const bodyRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (detailOpen) return;
    const body = bodyRef.current, strip = stripRef.current;
    if (!body || !strip) return;

    // On a fresh load the rails are still empty — the catalog is on its way —
    // so the body is not yet tall enough to scroll and this assignment clamps
    // straight back to 0, leaving the strip sitting in view. Re-apply it as
    // the rails fill in, and give up the moment it takes or the reader
    // scrolls for themselves.
    let settled = false;
    const hide = () => {
      if (settled) return;
      body.scrollTop = strip.offsetHeight;
      if (body.scrollTop >= strip.offsetHeight) stop();
    };
    const stop = () => { settled = true; ro.disconnect(); };
    const ro = new ResizeObserver(hide);
    for (const child of Array.from(body.children)) ro.observe(child);
    hide();

    body.addEventListener('touchstart', stop, { passive: true });
    body.addEventListener('wheel', stop, { passive: true });
    return () => {
      ro.disconnect();
      body.removeEventListener('touchstart', stop);
      body.removeEventListener('wheel', stop);
    };
  }, [detailOpen]);

  // Pulled down to peek at the strip and then left alone, the page eases back
  // to its resting scroll on its own: a glance, not a new top of the page.
  // Nothing moves while a finger is still on the screen.
  useEffect(() => {
    if (detailOpen) return;
    const body = bodyRef.current, strip = stripRef.current;
    if (!body || !strip) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let touching = false;
    const arm = () => {
      clearTimeout(timer);
      if (touching || body.scrollTop >= strip.offsetHeight) return;
      timer = setTimeout(() => {
        if (!touching && body.scrollTop < strip.offsetHeight) {
          body.scrollTo({ top: strip.offsetHeight, behavior: 'smooth' });
        }
      }, STRIP_PEEK_MS);
    };
    const onTouchStart = () => { touching = true; clearTimeout(timer); };
    const onTouchEnd = () => { touching = false; arm(); };
    body.addEventListener('scroll', arm, { passive: true });
    body.addEventListener('touchstart', onTouchStart, { passive: true });
    body.addEventListener('touchend', onTouchEnd, { passive: true });
    body.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      clearTimeout(timer);
      body.removeEventListener('scroll', arm);
      body.removeEventListener('touchstart', onTouchStart);
      body.removeEventListener('touchend', onTouchEnd);
      body.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [detailOpen]);

  // The feed grows as the reader reaches the foot of it. Once a section has
  // run out the page stops getting taller, and asking for more would just
  // re-render on every scroll event, so the last height it grew to is the
  // signal to stop asking.
  useEffect(() => {
    if (detailOpen) return;
    const body = bodyRef.current;
    if (!body) return;
    let asked = 0;
    const onScroll = () => {
      if (body.scrollTop + body.clientHeight < body.scrollHeight - NEAR_BOTTOM_PX) return;
      if (body.scrollHeight === asked) return;
      asked = body.scrollHeight;
      setShown((n) => n + PAGE);
    };
    body.addEventListener('scroll', onScroll, { passive: true });
    return () => body.removeEventListener('scroll', onScroll);
  }, [detailOpen, tab]);

  return (
    <div className="page-view">
      <ViewHeader
        icon={
          // The reel boards' mark (ReelBoard.tsx), dot for dot: fixed purple
          // with each dot's own opacity, no ring around it.
          <svg width={34} height={34} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="4" fill="#a06bff" />
            <circle cx="6" cy="7" r="2.5" fill="#a06bff" opacity=".72" />
            <circle cx="18" cy="7" r="2" fill="#a06bff" opacity=".66" />
            <circle cx="5" cy="16" r="1.5" fill="#a06bff" opacity=".6" />
            <circle cx="19" cy="15" r="3" fill="#a06bff" opacity=".78" />
            <circle cx="12" cy="4" r="1.5" fill="#a06bff" opacity=".6" />
            <circle cx="8" cy="19" r="2" fill="#a06bff" opacity=".72" />
          </svg>
        }
        // The reel boards' lockup (see .rb-lockup in reels.css), at header scale.
        title="Grappling Gnosis"
        className="view-header--home"
        action={
          // The How To screen lives up here as an icon, so Home's body is
          // all content.
          <div className="home-header-btns">
            <button
              type="button"
              className="changelog-btn"
              onClick={() => setHowToOpen(true)}
              aria-label="How To"
              title="How To"
            >
              <CircleHelp size={20} />
            </button>
          </div>
        }
      />
      {/* An open screen (How To, creators) fills the body; otherwise the rails
          stack and scroll. */}
      <div className={`page-view-body${detailOpen ? '' : ' home-body'}`} ref={bodyRef}>
        {howToOpen && <TutorialsPage entry={HOW_TO} onBack={() => setHowToOpen(false)} guide={guide} />}
        {creators.open && (
          <Spotlights
            key={creators.handle ?? 'list'}
            title="Spotlight"
            icon={<Spotlight size={17} />}
            onBack={() => setCreators({ open: false })}
            library={library}
            initialCreator={creators.handle}
          />
        )}
        {sponsor && (
          <SponsorPage promo={sponsor} library={library} onBack={() => setSponsor(null)} />
        )}
        {!detailOpen && (
          <>
            {/* The rails stack, and the referral band sits flush under the
                last one. */}
            {/* Tucked above the top of the page: the body opens scrolled just
                past it, so it shows only when the page is pulled down. */}
            <div className="home-version-strip" ref={stripRef}>
              <span className="home-version">Version {__APP_VERSION__}</span>
            </div>
            <div className="home-rails">
              <TopCreators
                onOpen={(handle) => setCreators({ open: true, handle })}
                onSponsor={(promo) => setSponsor(promo)}
                onPromote={() => (guest ? requestAccount() : setPromoteOpen(true))}
              />
              {/* The section labels, one row of them, each opening its own
                  feed underneath. A guest has no watch history to show. */}
              <div className="home-tabs" role="tablist">
                {TABS.filter((t) => !(t.account && guest)).map((t) => (
                  <button
                    type="button"
                    key={t.id}
                    role="tab"
                    aria-selected={tab === t.id}
                    className={`home-tab${tab === t.id ? ' home-tab--on' : ''}`}
                    onClick={() => {
                      setTab(t.id);
                      // A new section starts at the top of itself, and at the
                      // depth every section starts at.
                      setShown(PAGE);
                      const strip = stripRef.current;
                      if (bodyRef.current && strip) bodyRef.current.scrollTop = strip.offsetHeight;
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {tab === 'new' && <Newest library={library} feed={shown} />}
              {tab === 'popular' && <MostStarred library={library} feed={shown} />}
              {tab === 'upcoming' && <Upcoming feed={shown} />}
              {tab === 'recent' && !guest && <RecentlyWatched library={library} feed={shown} />}
            </div>
            {!guest && <ReferralPanel user={user} />}
          </>
        )}
        {promoteOpen && (
          <PromoteModal
            existing={sub.promotion}
                        onClose={() => setPromoteOpen(false)}
            onDone={() => setPromoteOpen(false)}
          />
        )}
      </div>
    </div>
  );
}
